import React, { useState } from "react";
import Pagination from "react-js-pagination";
import { IoSearchOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import { toast } from "react-toastify";
import {
  exportOdaSheetAPI,
  searchServiceablePincodesAPI,
} from "services/user-controllers/onboardingController";
import "./Onboarding.scss";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-----";

// Pincode Serviceability Check: search by pincode / city / state, results listed like the
// vendor panel's pincode table; Export downloads every serviceable pincode with its type.
const PincodeCheck = () => {
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [odaCount, setOdaCount] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchPage = async (search, nextPage, nextLimit) => {
    try {
      setLoading(true);
      const { data } = await searchServiceablePincodesAPI({ search, page: nextPage, limit: nextLimit });
      if (data?.code === 200) {
        setRows(data.data.pincodes || []);
        setTotal(data.data.totalCount || 0);
        setOdaCount(data.data.odaCount ?? null);
        setPage(nextPage);
        setSearched(search);
        setHasSearched(true);
      } else {
        toast.error(data?.message || "Search failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchPage(query.trim(), 1, limit);
  };

  const exportOda = async () => {
    try {
      setExporting(true);
      const { data, status } = await exportOdaSheetAPI();
      if (status !== 200) return toast.error("Failed to download the file.");
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([data]));
      link.download = "serviceable_pincodes.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while downloading the file.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ob_card ob_pin">
      <div className="ob_pin_head">
        <div>
          <h5>
            <IoSearchOutline /> Pincode Serviceability Check
          </h5>
          <p>Search by pincode, city, or state to check zone &amp; serviceability status.</p>
        </div>
        <button type="button" className="ob_btn outline orange" disabled={exporting} onClick={exportOda}>
          <MdOutlineFileDownload /> Export Pincode Sheet{odaCount !== null ? ` (${odaCount})` : ""}
        </button>
      </div>

      <form className="ob_pin_search" onSubmit={onSearch}>
        <div className="box">
          <IoSearchOutline />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by pincode (e.g. 400001), city (Mumbai) or state (Gujarat)"
          />
        </div>
        <button type="submit" className="ob_btn primary" disabled={loading}>
          Search
        </button>
      </form>

      {hasSearched && (
        <>
          <div className="ob_matrix_wrap">
            <table className="ob_charges ob_pin_table">
              <thead>
                <tr>
                  <th>State Name</th>
                  <th>City Name</th>
                  <th>Zone Name</th>
                  <th>Pincode</th>
                  <th>Created Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.state}</td>
                    <td>{r.city}</td>
                    <td>{r.zoneName || "-----"}</td>
                    <td>{r.pincode}</td>
                    <td>{fmtDate(r.createdAt)}</td>
                    <td>
                      <span className={`tag ${r.status === "serviceable" ? "ok" : r.status === "Non-Serviceable" ? "bad" : "oda"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={6} className="empty">
                      No pincodes found{searched ? ` for “${searched}”` : ""}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="ob_pin_pager">
              <label>
                Rows per page{" "}
                <select
                  value={limit}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setLimit(next);
                    fetchPage(searched, 1, next);
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <Pagination
                itemClass="page-item"
                linkClass="page-link"
                pageRangeDisplayed={5}
                activePage={page}
                onChange={(p) => fetchPage(searched, p, limit)}
                itemsCountPerPage={limit}
                totalItemsCount={total}
                itemClassLast={"last_page next"}
                itemClassFirst={"first_page prev"}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PincodeCheck;
